import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { useSignalRCallScribeEvents } from 'hooks/useSignalRCallScribeEvents';
import { resetLiveTranscripts } from 'store/callScribe';
import { CallScribeActiveTranscriptCallSelector } from 'store/callScribe/selectors';
import LiveAssistFrame from 'components/Alpha/LiveAssist/LiveAssistFrame';
import LiveAssistPanel from 'components/Alpha/LiveAssist/LiveAssistPanel';
import { useLiveAssistTarget } from 'components/Alpha/LiveAssist/liveAssistTarget';
import PopupCallView from 'components/CallScribe/PopupCallView';
import { useTwilioDevice } from 'components/CallScribe/useTwilioDevice';

/**
 * Top-level host rendered inside the dedicated softphone popup window.
 *
 * The popup loads the same Pod URL with `?softphone=popup&to=<E164>` appended. We
 * deliberately bypass the regular Pod chrome (Sidebar, integration page, tab system)
 * because the popup's only job is to host the browser-side Twilio call leg in a
 * top-level window where `getUserMedia` will actually prompt the user.
 *
 * `useTwilioDevice` is owned here (rather than inside `PopupCallView`) to keep the
 * call-view component pure and easier to render-test.
 */
export interface ISoftphonePopupHostProps {
  launchedByPod: boolean;
  signedInAs?: string;
}

const SoftphonePopupHost: React.FC<ISoftphonePopupHostProps> = ({ launchedByPod, signedInAs }) => {
  useSignalRCallScribeEvents();
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(resetLiveTranscripts());
  }, [dispatch]);
  const twilio = useTwilioDevice();
  const transcriptCallId = useAppSelector(CallScribeActiveTranscriptCallSelector);
  const callLive = twilio.state === 'connecting' || twilio.state === 'in-call';
  const target = useLiveAssistTarget(callLive ? transcriptCallId : undefined, undefined);
  return (
    <LiveAssistFrame>
      <PopupCallView twilio={twilio} launchedByPod={launchedByPod} signedInAs={signedInAs} />
      <LiveAssistPanel callId={target.callId} mode={target.mode} attempt={target.attempt} />
    </LiveAssistFrame>
  );
};

export default SoftphonePopupHost;
